import GapBadge from "./GapBadge";
import {
  LEADERBOARD_COLUMN_WIDTHS,
  LEADERBOARD_TRAILING_EQUAL_WIDTH,
  LEADERBOARD_TABLE_COLUMNS,
  colAlignClass,
  leaderboardRowClassName,
  type LeaderboardRow,
  type LeaderboardSortState,
} from "./leaderboardTableUtils";
import {
  LB_RANK_BADGE,
  LB_RANK_MEDAL,
  LB_SORT_IND,
  LB_SPONSOR_NAME,
  LB_TABLE,
  LB_TABLE_CARD,
  LB_TABLE_WRAP,
  LB_TH,
  LB_TH_SORTED,
  fmtScore,
  leaderboardDeltaClass,
  leaderboardRowCellClassName,
  tierDotClass,
  tierPillClass,
} from "../../Utils/Helper/sponsorEngagementUi";

const TierPill = ({ tier }: { tier: string }) => (
  <span className={tierPillClass(tier)}>
    <span className={tierDotClass(tier)} />
    {tier}
  </span>
);

const RankCell = ({ rank }: { rank?: number }) => {
  if (rank == null) return <span className="text-gray-400">—</span>;
  if (rank === 1) return <span className={LB_RANK_MEDAL}>🥇</span>;
  if (rank === 2) return <span className={LB_RANK_MEDAL}>🥈</span>;
  if (rank === 3) return <span className={LB_RANK_MEDAL}>🥉</span>;
  return <span className={LB_RANK_BADGE}>{rank}</span>;
};

const DeltaPriorCell = ({ delta }: { delta?: number | null }) => {
  if (delta == null || Number.isNaN(delta)) {
    return <span className="text-gray-400">—</span>;
  }
  if (Math.abs(delta) < 0.05) {
    return <span className={leaderboardDeltaClass("flat")}>→ 0.00</span>;
  }
  if (delta > 0) {
    return (
      <span className={leaderboardDeltaClass("up")}>▲ +{delta.toFixed(2)}</span>
    );
  }
  return (
    <span className={leaderboardDeltaClass("down")}>▼ {delta.toFixed(2)}</span>
  );
};

const cellClass = (
  rank: number | undefined,
  align: "center" | "left" | "right",
  key?: string
) => {
  const base = `${leaderboardRowCellClassName(rank)} ${colAlignClass(align)}`;
  // Rank column is very narrow; trim its padding and disable ellipsis so the
  // medal/badge renders cleanly without a trailing "…".
  return key === "rank" ? `${base} !px-1 text-clip` : base;
};

const renderColGroup = () => (
  <colgroup>
    {LEADERBOARD_TABLE_COLUMNS.map(col => (
      <col
        key={col.key}
        className={
          LEADERBOARD_COLUMN_WIDTHS[col.key] ?? LEADERBOARD_TRAILING_EQUAL_WIDTH
        }
      />
    ))}
  </colgroup>
);

const renderCell = (
  row: LeaderboardRow,
  key: string,
  onRowClick?: (row: LeaderboardRow) => void
) => {
  switch (key) {
    case "rank":
      return <RankCell rank={row.rank} />;
    case "sponsor_name":
      return (
        <div className="min-w-0">
          <div className={`${LB_SPONSOR_NAME}`}>
            {row.sponsor_name || row.name || "—"}
          </div>
        </div>
      );
    case "theater":
      return row.theater || "—";
    case "region":
      return row.region || "—";
    case "p1":
      return fmtScore(row.p1, 2);
    case "p2":
      return fmtScore(row.p2, 2);
    case "composite":
      return <strong>{fmtScore(row.composite, 2)}</strong>;
    case "tier":
      return row.tier ? (
        <div className="flex justify-start">
          <TierPill tier={row.tier} />
        </div>
      ) : (
        "—"
      );
    case "delta":
      return <DeltaPriorCell delta={row.delta} />;
    case "gap":
      return (
        <div className="flex justify-start">
          <GapBadge gap={row.gap} />
        </div>
      );
    default:
      return "—";
  }
};

type LeaderboardTableProps = {
  rows: LeaderboardRow[];
  sortable?: boolean;
  sort?: LeaderboardSortState;
  onSortToggle?: (col: string) => void;
  onRowClick?: (row: LeaderboardRow) => void;
  loading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
};

const LeaderboardTable = ({
  rows,
  sortable = false,
  sort,
  onSortToggle,
  onRowClick,
  loading = false,
  loadingMessage = "Loading leaderboard…",
  emptyMessage = "No sponsors match the current filters.",
}: LeaderboardTableProps) => (
  <div className={LB_TABLE_CARD}>
    <div className={LB_TABLE_WRAP}>
      <table className={LB_TABLE}>
        {renderColGroup()}
        <thead>
          <tr>
            {LEADERBOARD_TABLE_COLUMNS.map(c => {
              const sortedHere = sortable && sort?.col === c.key;
              const ind = sortedHere ? (sort?.dir === "desc" ? "▼" : "▲") : "";
              return (
                <th
                  key={c.key}
                  className={`${LB_TH} ${colAlignClass(c.align)} ${sortedHere ? LB_TH_SORTED : ""}`}
                  onClick={
                    sortable && onSortToggle
                      ? () => onSortToggle(c.key)
                      : undefined
                  }
                >
                  {c.label}
                  {sortable ? <span className={LB_SORT_IND}>{ind}</span> : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={LEADERBOARD_TABLE_COLUMNS.length}
                className="text-center py-12"
              >
                <div className="flex flex-col items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-purple-600" />
                  <p className="mb-0 mt-3 text-sm text-gray-600">
                    {loadingMessage}
                  </p>
                </div>
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td
                colSpan={LEADERBOARD_TABLE_COLUMNS.length}
                className="text-center py-8 text-gray-500"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map(row => (
              <tr
                key={row.sponsor_id || row.sponsor_name || String(row.rank)}
                className={leaderboardRowClassName(row.rank)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {LEADERBOARD_TABLE_COLUMNS.map(col => (
                  <td
                    key={col.key}
                    className={cellClass(row.rank, col.align, col.key)}
                  >
                    {renderCell(row, col.key, onRowClick)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export default LeaderboardTable;
